/**
 * The Arche Store snapshot's data handoff, client side (ADR-043; the element
 * is written by the store's snapshot publisher, its format lives in
 * `@fias/public-site-ui` `store/handoff.ts`) — the Games page's arrangement
 * (`games-snapshot-data.ts`), for the store.
 *
 * A cold load of a store page arrives as a static snapshot. The app mounts
 * over it and would otherwise draw loading states until its requests answer,
 * so the snapshot also carries the data it was drawn from. This module reads
 * that element ONCE (and removes it), and hands each query its own data:
 *
 *   - only for the page the snapshot was drawn for (`path` equals the address
 *     the app loaded at), so it can never seed another page;
 *   - only once, so a later visit to the page loads fresh data;
 *   - as already-stale data (`STORE_SNAPSHOT_DATA_UPDATED_AT`), so React
 *     Query paints it at once and refreshes it in the background.
 *
 * Anything malformed is ignored and the page simply loads as it would without
 * a snapshot. Lives in `lib/` because the store's availability store
 * (`store-availability.ts`) reads it too.
 */
import {
  STORE_SNAPSHOT_DATA_ELEMENT_ID,
  STORE_SNAPSHOT_DATA_VERSION,
  type StoreAppPageData,
  type StoreAppsPageData,
  type StoreCategoryRef,
  type StoreCollectionData,
  type StoreHomeData,
  type StorePageData,
  type StoreTopData,
} from '@fias/public-site-ui';

/** When the handed-over data counts as fetched: never — refresh it in the background. */
export const STORE_SNAPSHOT_DATA_UPDATED_AT = 0;

/** A category's apps per page of the paged query (the API's most). */
const APPS_PAGE_SIZE = 60;

interface Handoff {
  path: string;
  data: StorePageData;
  taken: boolean;
}

/** `undefined` until the element has been looked for. */
let handoff: Handoff | null | undefined;

function withoutTrailingSlash(path: string): string {
  return path.length > 1 ? path.replace(/\/+$/, '') : path;
}

function isPageData(value: unknown): value is StorePageData {
  if (!value || typeof value !== 'object') return false;
  const data = value as Record<string, unknown>;
  switch (data.kind) {
    case 'home':
      return !!data.home && Array.isArray((data.home as StoreHomeData).topApps);
    case 'categories':
      return Array.isArray(data.categories);
    case 'top':
      return Array.isArray((data.top as StoreTopData)?.cards) && Array.isArray(data.categories);
    case 'category':
      return (
        typeof data.categoryId === 'string' &&
        Array.isArray((data.page as StoreAppsPageData)?.cards)
      );
    case 'story':
      return (
        typeof data.slug === 'string' && Array.isArray((data.story as StoreCollectionData)?.items)
      );
    case 'app':
      return typeof data.slug === 'string' && !!(data.app as StoreAppPageData)?.card;
    default:
      return false;
  }
}

function read(): Handoff | null {
  if (handoff !== undefined) return handoff;
  handoff = null;
  if (typeof document === 'undefined') return null;
  const element = document.getElementById(STORE_SNAPSHOT_DATA_ELEMENT_ID);
  if (!element) return null;
  element.remove();
  try {
    const parsed = JSON.parse(element.textContent ?? '') as Record<string, unknown>;
    if (
      parsed.v === STORE_SNAPSHOT_DATA_VERSION &&
      typeof parsed.path === 'string' &&
      isPageData(parsed.data)
    ) {
      handoff = { path: withoutTrailingSlash(parsed.path), data: parsed.data, taken: false };
    }
  } catch {
    // Not ours to fix: the page loads as it would with no snapshot.
  }
  return handoff;
}

/** Whether this page load arrived as a store snapshot (with readable data). */
export function arrivedWithStoreSnapshot(): boolean {
  return read() !== null;
}

/** The handed-over data, once, if it is this page's and of this kind (and key). */
function take<K extends StorePageData['kind']>(
  kind: K,
  key?: string,
): Extract<StorePageData, { kind: K }> | undefined {
  const current = read();
  if (!current || current.taken) return undefined;
  if (typeof window === 'undefined') return undefined;
  if (withoutTrailingSlash(window.location.pathname) !== current.path) return undefined;
  const { data } = current;
  if (data.kind !== kind) return undefined;
  if (data.kind === 'category' && data.categoryId !== key) return undefined;
  if ((data.kind === 'story' || data.kind === 'app') && data.slug !== key) return undefined;
  current.taken = true;
  return data as Extract<StorePageData, { kind: K }>;
}

/** Today's or the Apps tab's data, if this load arrived as one of them. */
export function takeStoreHomeSnapshot(): StoreHomeData | undefined {
  return take('home')?.home;
}

export function takeStoreCategoriesSnapshot(): { categories: StoreCategoryRef[] } | undefined {
  const data = take('categories');
  return data ? { categories: data.categories } : undefined;
}

/** The Top chart's data (all apps, no category), if this load arrived as it. */
export function takeStoreTopSnapshot(): StoreTopData | undefined {
  return take('top')?.top;
}

/**
 * A category's apps (most popular first, as the snapshot lists every one), in
 * the shape of the paged category query.
 */
export function takeStoreCategorySnapshot(
  categoryId: string,
): { pages: StoreAppsPageData[]; pageParams: number[] } | undefined {
  const data = take('category', categoryId);
  if (!data) return undefined;
  const { page } = data;
  const pages: StoreAppsPageData[] = [];
  for (let start = 0; start === 0 || start < page.cards.length; start += APPS_PAGE_SIZE) {
    pages.push({ ...page, cards: page.cards.slice(start, start + APPS_PAGE_SIZE) });
  }
  return { pages, pageParams: pages.map((_, i) => i * APPS_PAGE_SIZE) };
}

export function takeStoreStorySnapshot(slug: string): StoreCollectionData | undefined {
  return take('story', slug)?.story;
}

export function takeStoreAppSnapshot(slug: string): StoreAppPageData | undefined {
  return take('app', slug)?.app;
}

/** Test hook: forget what was read, so the next call looks for the element again. */
export function __resetStoreSnapshotDataForTests(): void {
  handoff = undefined;
}
